/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import * as Im from 'immutable';
import { Typography } from '@rbx/ui';
import useTryItOut from '../hooks/useTryItOut';

// https://github.com/swagger-api/swagger-ui/blob/04b9ad7b37f02bce0e1cb8c521e40b68585ccf9f/src/core/utils/index.js
const getList = (iterable: any, keys: any) => {
  if (!Im.Iterable.isIterable(iterable)) {
    return Im.List();
  }
  const val = iterable.getIn(Array.isArray(keys) ? keys : [keys]);
  return Im.List.isList(val) ? val : Im.List();
};

// https://github.com/swagger-api/swagger-ui/blob/04b9ad7b37f02bce0e1cb8c521e40b68585ccf9f/src/core/components/operation.jsx
const DetailsSwaggerUIOperation = ({
  specPath,
  operation: operationProps,
  response,
  request,

  onResetClick,
  onExecute,

  getComponent,
  specSelectors,
  specActions,
  oas3Actions,
  oas3Selectors,
  fn,
  getConfigs,
}: any) => {
  const { tryItOutAllowed, tryItOutOpen: tryItOutEnabled } = useTryItOut();

  const { path, method, executeInProgress } = operationProps.toJS();

  // Extract operation data for rendering
  const operation = operationProps.getIn(['op']);
  const responses = operation.get('responses');
  const parameters = getList(operation, ['parameters']);
  const { schemes } = operation;
  const operationScheme = specSelectors.operationScheme(path, method);

  // Gather components inherited from swagger-ui
  const Parameters = getComponent('parameters');
  const Schemes = getComponent('schemes');
  const Execute = getComponent('execute');
  const Clear = getComponent('clear');
  const Responses = getComponent('responses');

  if (responses && response && response.size > 0) {
    const notDocumented =
      !responses.get(String(response.get('status'))) && !responses.get('default');
    // eslint-disable-next-line no-param-reassign
    response = response.set('notDocumented', notDocumented);
  }

  const onChangeKey = [path, method];

  const validationErrors = specSelectors.validationErrors([path, method]);

  return (
    <div className='opblock'>
      <div className='opblock-body'>
        {!operation || !operation.size ? null : (
          <Parameters
            parameters={parameters}
            specPath={specPath.push('parameters')}
            operation={operation}
            onChangeKey={onChangeKey}
            onTryoutClick={() => {}} // Controlled by external hook
            onResetClick={onResetClick}
            onCancelClick={() => {}} // Controlled by external hook
            tryItOutEnabled={tryItOutEnabled}
            allowTryItOut={tryItOutAllowed}
            fn={fn}
            getComponent={getComponent}
            specActions={specActions}
            specSelectors={specSelectors}
            pathMethod={[path, method]}
            getConfigs={getConfigs}
            oas3Actions={oas3Actions}
            oas3Selectors={oas3Selectors}
          />
        )}

        {(!tryItOutEnabled || !tryItOutAllowed) && schemes && schemes.size ? (
          <div className='opblock-schemes'>
            <Schemes
              schemes={schemes}
              path={path}
              method={method}
              specActions={specActions}
              currentScheme={operationScheme}
            />
          </div>
        ) : null}

        {!tryItOutEnabled || validationErrors.length <= 0 ? null : (
          <div className='validation-errors errors-wrapper'>
            <Typography variant='body2' color='error'>
              Please correct the following validation errors and try again.
            </Typography>
            <ul>
              {validationErrors.map((error: string, index: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`validation-error-${error.substring(0, 20)}-${index}`}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div
          className={
            !tryItOutEnabled || !response || !tryItOutAllowed ? 'execute-wrapper' : 'btn-group'
          }>
          {!tryItOutEnabled || !tryItOutAllowed ? null : (
            <Execute
              operation={operation}
              specActions={specActions}
              specSelectors={specSelectors}
              oas3Selectors={oas3Selectors}
              oas3Actions={oas3Actions}
              path={path}
              method={method}
              onExecute={onExecute}
              disabled={executeInProgress}
            />
          )}

          {!tryItOutEnabled || !response || !tryItOutAllowed ? null : (
            <Clear specActions={specActions} path={path} method={method} />
          )}
        </div>

        {executeInProgress ? (
          <div className='loading-container'>
            <div className='loading' />
          </div>
        ) : null}

        {!responses ? null : (
          <Responses
            responses={responses}
            request={request}
            tryItOutResponse={response}
            getComponent={getComponent}
            getConfigs={getConfigs}
            specSelectors={specSelectors}
            oas3Actions={oas3Actions}
            oas3Selectors={oas3Selectors}
            specActions={specActions}
            produces={specSelectors.producesOptionsFor([path, method])}
            producesValue={specSelectors.currentProducesFor([path, method])}
            specPath={specPath.push('responses')}
            path={path}
            method={method}
            displayRequestDuration
            fn={fn}
          />
        )}
      </div>
    </div>
  );
};

export default DetailsSwaggerUIOperation;
